import useUrlState from '@ahooksjs/use-url-state'
import { FieldTimeOutlined, SwapOutlined } from '@ant-design/icons'
import { useInViewport } from 'ahooks'
import { Avatar, Button, Menu, Result, Tabs, Tooltip } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useRouteMatch } from 'react-router-dom'

import { AppHeader, ColumnLayout, PostTypeIcon, UserAvatarGroup } from '@/components'
import {
  PostType,
  useGetUserFollowingStatusQuery,
  useGetUserQuery,
  useGetViewerQuery,
  useVisitPageMutation,
} from '@/generated/graphql'
import { useLayoutSize } from '@/hooks/layout-size.hooks'
import { appState } from '@/modules/app/app.state'
import { Follow } from '@/modules/follow'
import { ThanksMessageModal } from '@/modules/post/thanks-post/thanks-message.component'
import { ProfileContainer } from '@/modules/user/profile/profile.container'
import { UserManage } from '@/modules/user/user-manage/user-manage.component'
import { routes } from '@/routes'

import { useIsSystemAdmin } from '../admin/admin.hooks'
import { UserInfoHeader } from './user-info/user-info-header/user-info-header.component'
import { UserInfoContainer } from './user-info/user-info.container'
import { UserSidebar } from './user-sidebar/user-sidebar.component'
import { UserTimeline } from './user-timeline/user-timeline.component'
import s from './user.module.less'

interface State {
  tab: string | undefined
}

export const UserContainer = () => {
  const avatarMaxCount = 10
  const history = useHistory()
  const { t } = useTranslation()

  const [isUserInfoPageVisible, setIsUserInfoPageVisible] = React.useState(true)

  const headerRef = React.useRef<HTMLDivElement>(null)
  const isHeaderInView = useInViewport(() => headerRef.current)

  const match = useRouteMatch<{ userId: string }>('/users/:userId')
  const isNameRouting = isNaN(Number(match?.params.userId))

  const userParams = {
    ...(isNameRouting && { userName: match?.params.userId }),
    ...(!isNameRouting && { userId: Number(match?.params.userId) }),
  }

  const { data: meData } = useGetViewerQuery()
  const { data, error } = useGetUserQuery({
    variables: {
      ...userParams,
      withPageVisitors: true,
      pageVisitorPagination: {
        last: avatarMaxCount,
      },
    },
  })

  const { data: isFollowingData } = useGetUserFollowingStatusQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    variables: {
      ...userParams,
    },
  })

  const [setVisitPage] = useVisitPageMutation()

  const [state, setState] = useUrlState<State>({
    tab: 'profile',
  })

  const { isMobileSize } = useLayoutSize()
  const isSystemAdmin = useIsSystemAdmin()

  const user = data?.user
  const isFollowingStatus = isFollowingData?.user
  const viewedUsers = data?.user.viewedUsers!.edges.map(({ node }) => node).reverse() || []
  const viewedUserTotalCount = data?.user.viewedUsers!.totalCount || 0

  React.useEffect(() => {
    if (meData?.viewer.userId === user?.userId) {
      history.push({
        pathname: routes.me.home(),
      })
    }

    if (user?.userId) {
      setVisitPage({
        variables: {
          userId: user!.userId,
        },
      })
    }
  }, [user])

  React.useEffect(() => {
    appState({ ...appState(), showSidebar: false })
    return () => {
      appState({ ...appState(), showSidebar: true })
    }
  }, [])

  if (error) {
    return (
      <div style={{ height: '90vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Result
          status='404'
          title={t('page.user.notFound')}
          extra={
            <Button type='primary' onClick={() => history.goBack()}>
              Back
            </Button>
          }
        />
      </div>
    )
  }

  if (!user || !isFollowingStatus) {
    return null
  }

  const isFollowing = isFollowingStatus.isFollowing
  const isFollowingEachOther = isFollowingStatus.isFollowingEachOther

  const pageSwitch = state.tab === 'profile' && isSystemAdmin && (
    <Button
      type='primary'
      ghost
      style={{ marginRight: 12, display: 'none' }}
      icon={<SwapOutlined />}
      onClick={() => setIsUserInfoPageVisible((value) => !value)}
    />
  )

  const actions = (
    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row-reverse' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {pageSwitch}
        <ThanksMessageModal receiver={user}>
          <Tooltip title={t('post.thanks.thanks')}>
            <Button
              style={{ marginRight: 12 }}
              type='primary'
              ghost
              icon={<PostTypeIcon type={PostType.Thanks} withText={false} />}
            />
          </Tooltip>
        </ThanksMessageModal>
        <Tooltip title={user.isFollowing ? t('follow.unfollow') : t('follow.follow')}>
          <Follow
            ghost={user.isFollowing}
            display={isMobileSize ? 'icon' : 'both'}
            followeeUserId={user.userId}
            isFollowingEachOther={isFollowingEachOther}
            isFollowing={isFollowing}
          />
        </Tooltip>
      </div>
      {!isMobileSize && viewedUsers.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginRight: 12 }}>
          <Tooltip title={t('pageVisit.history')}>
            <FieldTimeOutlined style={{ fontSize: 20, color: '#48bbb5', marginRight: 8, cursor: 'pointer' }} />
          </Tooltip>
          <UserAvatarGroup
            users={viewedUsers}
            totalCount={viewedUserTotalCount}
            showCount={avatarMaxCount}
            userId={user.userId}
            title={t('pageVisit.history')}
            type='userPageVisitors'
            showSearch={false}
            avatarTooltipMessage={t('user.avatar.group.others', { count: viewedUserTotalCount - avatarMaxCount + 1 })}
          />
        </div>
      )}
    </div>
  )

  const name = user.name + (user.alias ? ` (${user.alias})` : '')

  const tabs = (
    <Tabs animated={false} activeKey={state.tab} onChange={(tab) => setState({ tab })} destroyInactiveTabPane>
      <Tabs.TabPane tab={t('personalMenu.mytimeline')} key='posts' />
      <Tabs.TabPane tab={t('personalMenu.profile')} key='profile' />
      <Tabs.TabPane tab={t('personalMenu.theirFollow')} key='follow' />
    </Tabs>
  )

  const header = (
    <>
      <div ref={headerRef}>
        <AppHeader
          title={!isUserInfoPageVisible && name}
          titleString={name}
          avatar={!isUserInfoPageVisible && <Avatar src={user.avatarUrl} />}
          userHeader={
            isUserInfoPageVisible && (
              <UserInfoHeader userId={user.userId} readonly={true}>
                <Menu
                  className={s.menu}
                  mode='horizontal'
                  selectedKeys={[state.tab]}
                  onClick={({ key: tab }) => setState({ tab })}
                >
                  <Menu.Item style={{ paddingLeft: 0 }} key='profile'>
                    {t('personalMenu.profile')}
                  </Menu.Item>
                  <Menu.Item key='posts'>{t('personalMenu.mytimeline')}</Menu.Item>
                  <Menu.Item key='follow'>{t('personalMenu.theirFollow')}</Menu.Item>
                </Menu>
              </UserInfoHeader>
            )
          }
          actions={actions}
          center={isUserInfoPageVisible}
        >
          {!isUserInfoPageVisible && tabs}
        </AppHeader>
      </div>
      {!isHeaderInView && (
        <AppHeader
          isFixed={!isHeaderInView}
          title={name}
          titleString={name}
          avatar={<Avatar size='large' src={user.avatarUrl} />}
          center={isUserInfoPageVisible}
        />
      )}
    </>
  )

  const middle = (() => {
    switch (state.tab) {
      case 'posts':
        return <UserTimeline userId={user.userId} />
      case 'profile':
        return isUserInfoPageVisible ? (
          <UserInfoContainer userId={user.userId} />
        ) : (
          <ProfileContainer userId={user.userId} />
        )
      case 'follow':
        return <UserManage userId={user.userId} />
    }
  })()

  const right = state.tab === 'posts' && <UserSidebar userId={user.userId} isFollowing={user.isFollowing} />

  return <ColumnLayout stickyHeader={false} header={header} middle={middle} right={right} />
}
